/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */

// Events
import {
  MODAL_CLOSE,
  MODAL_CLOSE_ALL,
  MODAL_CLOSED,
  MODAL_OPEN,
  MODAL_TOGGLE,
  MOUNT,
} from 'events';
import * as focusTrap from 'focus-trap';
import { component } from 'picoapp';
import {
  clearQueueScrollLocks,
  disablePageScroll,
  enablePageScroll,
  getScrollState,
} from 'scroll-lock';

import { setCSSProperty } from 'utils/css';

// Utils
import { hide, repaint, show } from 'utils/node';
import scrollElementToPosition from 'utils/scrollElementToPosition';
import { dispatch } from 'utils/theme';
import waitForTransitions from 'utils/waitForTransitions';

export default component((node, ctx) => {
  const focusTraps = new Map();
  const mountedModals = [...node.querySelectorAll('[data-modal]')].map(
    (modal) => modal.dataset.modal,
  );

  let openModals = [];
  let hasOpenModals = false;

  const getModal = (id) => {
    const selector = `[data-modal="${id}"]`;
    const modal = document.querySelector(selector);

    return modal;
  };

  const getTopFocusTrap = () => {
    const modalIds = [...openModals];
    const topModalId = modalIds.pop();

    if (!topModalId) return null;

    const topModal = getModal(topModalId);
    const topFocusTrap = focusTraps.get(topModal) || null;

    return topFocusTrap;
  };

  const pauseInactiveFocusTraps = (activeModal) => {
    focusTraps.forEach((trap, modal) => {
      if (modal !== activeModal) trap.pause();
    });
  };

  const closeModal = async (modal, id) => {
    const trap = focusTraps.get(modal);
    const enableScrollLock = modal.dataset.enableScrollLock === 'true';
    const dixaChatButton = document.querySelector('.dixa-messenger-namespace');

    modal.classList.remove('open');

    if (openModals.length === 0) {
      if (dixaChatButton) show(dixaChatButton);
      document.body.classList.remove('pdp-sticky-form-open');
      node.classList.remove('open');
      hasOpenModals = false;
    } else if (
      openModals.every((openModalId) => openModalId === 'main-product__sticky-form') &&
      dixaChatButton
    ) {
      show(dixaChatButton);
    }

    try {
      await waitForTransitions([modal, ...modal.querySelectorAll('[data-animated-element]')]);
    } catch (err) {
      console.error(err);
    }

    const { activeModals } = ctx.getState();
    if (activeModals.includes(id)) return;

    if (enableScrollLock) enablePageScroll(modal);
    trap?.deactivate();
    hide(modal);

    const topFocusTrap = getTopFocusTrap();
    topFocusTrap?.unpause();

    if (openModals.length === 0) {
      hide(node);

      // When a modal is closed and then another one is immediately
      // opened (programmatically), the enablePagescroll isnt' executed
      // as it's still waiting for transitions to end
      if (getScrollState() === false) clearQueueScrollLocks();
    }

    document.dispatchEvent(new Event('disable-auto-close-trigger-close'));
    document.dispatchEvent(new CustomEvent('modal:closed', { detail: { id } }));

    ctx.emit(MODAL_CLOSED, null, { id });
  };

  const openModal = (modal) => {
    const enableScrollLock = modal.dataset.enableScrollLock === 'true';

    if (enableScrollLock && !focusTraps.has(modal)) {
      focusTraps.set(modal, focusTrap.createFocusTrap(modal));
    }

    const trap = focusTraps.get(modal);
    pauseInactiveFocusTraps(modal);

    document.dispatchEvent(new Event('disable-auto-close-trigger-open'));
    setCSSProperty(modal, '--modal-z', openModals.length);

    show(modal);

    if (enableScrollLock) disablePageScroll(modal);

    if (hasOpenModals === false) {
      show(node);
      repaint(node);
      node.classList.add('open');
    } else {
      repaint(node);
    }

    hasOpenModals = true;
    modal.classList.add('open');
    trap?.activate();
  };

  const mountModal = (id, modal) => {
    mountedModals.push(id);
    node.appendChild(modal);
  };

  // eslint-disable-next-line
  ctx.on(MODAL_OPEN, async (state, { id, props = {} }) => {
    const dixaChatButton = document.querySelector('.dixa-messenger-namespace');

    if (openModals.includes(id)) return;

    if (id === 'cart' && props.action !== 'add_to_cart') {
      const { cart } = ctx.getState();

      if (cart) dispatch('view-cart', { cart });
    }

    const modal = getModal(id);

    // eslint-disable-next-line
    if (!modal) return console.error(`Couldn't find modal: ${id}`);

    const modalBox = document.querySelector(`[data-modal-box="${props.modalBox}"]`);
    const modalSections = [...modal.querySelectorAll(`[data-modal-section]`)];
    const scrollToElement = props.scrollTo ? document.querySelector(props.scrollTo) : modalBox;

    openModals.push(id);

    if (props.modalSection) {
      modalSections.forEach((section) => {
        section.hidden = section.dataset.modalSection !== props.modalSection;
      });
    }

    if (modalBox) {
      await scrollElementToPosition(scrollToElement, props.modalPosition || 'center');

      const { top, left, width, height } = modalBox.getBoundingClientRect();

      setCSSProperty(modal, '--modal-box-top', `${top}px`);
      setCSSProperty(modal, '--modal-box-left', `${left}px`);
      setCSSProperty(modal, '--modal-box-right', `${window.innerWidth - (left + width)}px`);
      setCSSProperty(modal, '--modal-box-bottom', `${window.innerHeight - (top + height)}px`);
    }

    if (!mountedModals.includes(id)) {
      mountModal(id, modal);
    }

    openModal(modal);

    ctx.hydrate({ activeModals: openModals });

    if (window._dixa_ && openModals.length > 0) {
      if (
        !openModals.every((openModalId) => openModalId === 'main-product__sticky-form') &&
        dixaChatButton
      ) {
        hide(dixaChatButton);
      }
    }

    if (openModals.every((openModalId) => openModalId === 'main-product__sticky-form')) {
      document.body.classList.add('pdp-sticky-form-open');
    }

    if (props?.modalBox?.includes('product_fit')) {
      dispatch('pdp-open-type-selector');
    } else if (props?.modalBox?.includes('product_size')) {
      dispatch('pdp-open-size-selector');
    } else if (id?.includes('whats-my-fit')) {
      dispatch('pdp-open-type-guide');
    } else if (id?.includes('size-guide') || id?.includes('size-chart')) {
      const tab = props.tab || 'sizing-chart';
      const sizeGuideRoot = modal.querySelector('[data-component="v2-size-guide"]');

      if (sizeGuideRoot) {
        sizeGuideRoot.dataset.activeTab = tab;
        sizeGuideRoot.querySelectorAll('[data-size-guide-tab]').forEach((button) => {
          const selected = button.dataset.sizeGuideTab === tab;
          button.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
      }

      if (id?.includes('size-chart')) {
        dispatch('suit-selector-open-size-chart', {
          suitParameter: props.suitParameter,
        });
      } else {
        dispatch('pdp-open-size-guide', { tab });
      }
    } else if (id?.includes('collection-filter-modal')) {
      dispatch('filter-open', {
        value: 'Filter & Sort',
      });
    } else if (id?.includes('suit-selector-modal')) {
      dispatch('suit-selector-open');
    }

    // eslint-disable-next-line
    return null;
  });

  ctx.on(MODAL_CLOSE, (state, { id }) => {
    if (!mountedModals.includes(id)) return;

    const modal = getModal(id);

    // eslint-disable-next-line
    if (!modal) return console.error(`Couldn't find modal: ${id}`);

    openModals = openModals.filter((openModalId) => openModalId !== id);

    ctx.hydrate({ activeModals: openModals });

    closeModal(modal, id);
  });

  ctx.on(MODAL_TOGGLE, (state, { id, props }) => {
    if (openModals.includes(id)) {
      ctx.emit(MODAL_CLOSE, null, { id });
    } else {
      ctx.emit(MODAL_OPEN, null, { id, props });
    }
  });

  ctx.on(MODAL_CLOSE_ALL, () => {
    const targetModals = [...openModals];
    openModals = [];
    targetModals.forEach((id) => {
      const modal = getModal(id);
      closeModal(modal, id);
    });

    ctx.hydrate({ activeModals: openModals });
  });

  ctx.on(MOUNT, () => {
    const modalLinks = [...document.querySelectorAll('[data-modal-toggle]')];

    modalLinks.forEach((link) => {
      const id = link.getAttribute('data-modal-toggle');
      let props;

      try {
        props = JSON.parse(link.getAttribute('data-modal-props') || '{}');
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
      }

      // Remove attribute to prevent multiple event listeners on the same element
      link.removeAttribute('data-modal-toggle');
      link.removeAttribute('data-modal-props');

      link.addEventListener('click', (e) => {
        if (link.nodeName === 'A') e.preventDefault();

        ctx.emit(MODAL_TOGGLE, null, { id, props });
      });
    });
  });

  const modalIdHashFormat = /#\{(.+?)\}/;
  const checkURLHash = (e) => {
    const [, hashModalId] = modalIdHashFormat.exec(window.location.hash) || [];
    if (!hashModalId) return;

    // Prevent scroll
    e.preventDefault();
    ctx.emit(MODAL_OPEN, null, { id: hashModalId });

    window.history.pushState({}, '', ' ');
  };

  window.addEventListener('hashchange', checkURLHash);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      ctx.emit(MODAL_CLOSE_ALL);
    }
  });
});
